import React from "react";
import {
  BriefcaseIcon,
  TrashIcon,
  PlusCircleIcon,
  NoSymbolIcon,
  PencilSquareIcon,
  EyeSlashIcon,
  InformationCircleIcon,
  MapPinIcon,
} from "@heroicons/react/24/outline";
import LocalBarOutlinedIcon from '@mui/icons-material/LocalBarOutlined';
import CustomTooltipComponent from "./custom_tooltip_component";


function stripUnitaFromName(name) {
  if (!name) return name;
  // rimuove " - Unità: qualsiasi_cosa" alla fine
  return name.replace(/\s*-\s*Unità:.*$/i, '');
}

function stripUnitaFromRefName(refName) {
  if (!refName) return refName;
  return refName.replace(/\s*-\s*Unità:.*$/i, '');
}


const MINUTES_IN_DAY = 24 * 60;

function parseTimeToMinutes(t) {
  if (!t || typeof t !== "string") return null;
  const [hh, mm] = t.split(":").map(Number);
  if (Number.isNaN(hh) || Number.isNaN(mm)) return null;
  return hh * 60 + mm;
}

function buildSegmentsFromShifts(shifts = [], isFisso = false) {
  const segments = [];

  shifts.forEach((shift) => {
    const isConfirmedShift = isFisso || !!shift.status;

    (shift.time_slots || []).forEach((slot) => {
      const startMin = parseTimeToMinutes(slot.start_at);
      const endMin = parseTimeToMinutes(slot.end_at);

      if (startMin == null || endMin == null) return;

      let s = Math.max(0, startMin);
      let e = Math.min(MINUTES_IN_DAY, endMin);

      if (e <= s) {
        e = MINUTES_IN_DAY;
      }

      segments.push({ startMin: s, endMin: e, isConfirmed: isConfirmedShift });
    });
  });

  return segments;
}

function mergeSegments(segments = []) {
  if (!segments.length) return [];

  const sorted = [...segments].sort((a, b) => a.startMin - b.startMin);

  const merged = [];
  let current = { ...sorted[0] };

  for (let i = 1; i < sorted.length; i++) {
    const seg = sorted[i];
    if (seg.startMin <= current.endMin) {
      current.endMin = Math.max(current.endMin, seg.endMin);
    } else {
      merged.push(current);
      current = { ...seg };
    }
  }
  merged.push(current);
  return merged;
}

export default function ShiftCard({
  workingHour,
  handleDeleteDailyWh,
  dipendente,
  formattedDay,
  selectedUnit,
  handlePlusCircleClick,
  handleEditOuPairing,
  handleConfirmSingleShift,
  dayBlocked,
  showBreaks,
  showWorkingAreas,
  onShiftDragStart,
  showDetails = true,
  dragMode,
}) {
  if (!workingHour) return null;

  const isFisso = workingHour.typology === "fisso";

  // segmenti con info di stato
  const rawSegments = buildSegmentsFromShifts(workingHour.shifts || [], isFisso);

  // separo confermati / da confermare e mergio per tipo
  const confirmedSegments = mergeSegments(
    rawSegments.filter((s) => s.isConfirmed)
  );
  const pendingSegments = mergeSegments(
    rawSegments.filter((s) => !s.isConfirmed)
  );

  return (
    <div className="rounded-lg bg-white h-full flex flex-col">
      {workingHour.typology === "senza_calendario" ? (
        <div className="text-center text-xs text-gray-700" />
      ) : Array.isArray(workingHour?.shifts) &&
        workingHour.shifts.length > 0 ? (
        <>
          {/* BARRA UNICA 0–24 */}
          {!showDetails && (
            <div className="px-2 pt-1 pb-2">
              <div className="flex justify-between text-[10px] text-gray-400 mb-1">
                <span>0</span>
                <span>6</span>
                <span>12</span>
                <span>18</span>
                <span>24</span>
              </div>
              <div className="relative h-1.5 rounded-full bg-gray-100 overflow-hidden">
                {/* prima gli arancioni (pending) */}
                {pendingSegments.map((seg, idx) => {
                  const left = (seg.startMin / MINUTES_IN_DAY) * 100;
                  const width =
                    ((seg.endMin - seg.startMin) / MINUTES_IN_DAY) * 100;
                  return (
                    <div
                      key={`p-${idx}`}
                      className="absolute inset-y-0 bg-orange-400 rounded-full"
                      style={{ left: `${left}%`, width: `${width}%` }}
                    />
                  );
                })}
                {/* poi i viola (confirmed) che si sovrappongono se serve */}
                {confirmedSegments.map((seg, idx) => {
                  const left = (seg.startMin / MINUTES_IN_DAY) * 100;
                  const width =
                    ((seg.endMin - seg.startMin) / MINUTES_IN_DAY) * 100;
                  return (
                    <div
                      key={`c-${idx}`}
                      className="absolute inset-y-0 bg-indigo-500 rounded-full"
                      style={{ left: `${left}%`, width: `${width}%` }}
                    />
                  );
                })}
              </div>
            </div>
          )}

          {/* CARD DEI TURNI */}
          {showDetails && workingHour.shifts
            .slice()
            .sort((a, b) => {
              const aStart =
                a.time_slots && a.time_slots[0]
                  ? parseTimeToMinutes(a.time_slots[0].start_at)
                  : 0;
              const bStart =
                b.time_slots && b.time_slots[0]
                  ? parseTimeToMinutes(b.time_slots[0].start_at)
                  : 0;
              return aStart - bStart;
            })
            .map((shift) => {
              const belongsToSelectedUnit = shift.ref_ou === selectedUnit;

              // bg sempre colorato, indipendente da showBreaks
              const slotBgClass =
                isFisso
                  ? "bg-indigo-100"
                  : shift.status
                  ? "bg-indigo-100"
                  : "bg-orange-100";

              return (
                <div
                  key={shift.dailyWorkingHourId}
                  draggable
                  onDragStart={(e) => {
                    if (dayBlocked) {
                      e.preventDefault();
                      return;
                    }

                    const cleanedName = stripUnitaFromName(shift.name || workingHour?.name || '');
                    const cleanedRefName = stripUnitaFromRefName(shift.ref_name || '');

                    const enrichedShift = {
                      ...shift,
                      name: cleanedName,
                      ref_name: cleanedRefName,
                      workingHourId: shift.workingHourId ?? null,
                    };
                    
                    const payload = {
                      type: "shift",
                      employeeId: dipendente.id,
                      employeeCf: dipendente.cf,
                      date: formattedDay,
                      dailyWorkingHourId: shift.dailyWorkingHourId,
                      shift: enrichedShift,
                    };
                    
                    e.dataTransfer.effectAllowed = "copy";
                    e.dataTransfer.setData(
                      "application/json",
                      JSON.stringify(payload)
                    );

                    onShiftDragStart?.(e, dipendente, formattedDay, enrichedShift);
                  }}
                  className={`
                    mb-2 border rounded-lg flex flex-col justify-between text-xs
                    ${
                      isFisso
                        ? "border-indigo-500 h-[180px]"
                        : shift.status
                        ? "border-indigo-500"
                        : "border-orange-400"
                    }
                    ${
                      selectedUnit && !belongsToSelectedUnit
                        ? "bg-gray-100"
                        : "bg-white"
                    }
                    ${!dragMode
                      ? "cursor-default"
                      : dragMode === "move"
                      ? "cursor-grab active:cursor-grabbing"
                      : "cursor-copy"
                    }
                  `}
                >
                  {/* HEADER: info + stato */}
                  <div className="flex items-center justify-between px-2 pt-1 pb-1">
                    <div className="flex items-center">
                      <CustomTooltipComponent
                        title={shift.ref_name || shift.name || 'Fascia oraria'}
                        placement="top"
                        arrow
                      >
                        <InformationCircleIcon className="w-4 h-4 text-gray-500 cursor-pointer hover:text-indigo-600" />
                      </CustomTooltipComponent>
                    </div>
                    {!isFisso && (
                      <div
                        className={`text-xs font-semibold flex justify-center mt-1 ${
                          shift.status ? "text-indigo-600" : "text-orange-500"
                        }`}
                      >
                        {shift.status ? "CONFERMATO" : "DA CONFERMARE"}
                      </div>
                    )}
                  </div>

                  {/* TIME SLOTS */}
                  <div
                    className="flex-grow px-2 py-1"
                    style={{ overflowY: "auto" }}
                  >
                    {Array.isArray(shift?.time_slots) &&
                    shift.time_slots.length > 0 ? (
                      shift.time_slots
                        .slice()
                        .sort((a, b) => a.start_at.localeCompare(b.start_at))
                        .map((slot, slotIndex) => (
                          <div key={slotIndex} className="mb-1">
                            <div
                              className={`
                                text-xs font-semibold text-gray-900 rounded-md px-2 py-1
                                ${slotBgClass}
                              `}
                            >
                              <BriefcaseIcon className="inline-block w-3 h-3 mr-1" />
                              {slot.start_at} - {slot.end_at}
                            </div>

                            {showBreaks &&
                              Array.isArray(slot.breaks) &&
                              slot.breaks.length > 0 && (
                                <div className="mt-1 text-xs text-gray-600">
                                  {slot.breaks.map(
                                    (breakTime, breakIndex) => (
                                      <div
                                        key={breakIndex}
                                        className={`flex items-center justify-center ${
                                          breakTime?.is_visible
                                            ? "text-gray-600"
                                            : "text-gray-400"
                                        }`}
                                      >
                                        <span>
                                          {!breakTime?.is_visible ? (
                                            <EyeSlashIcon
                                              className="inline-block mr-1 h-3 w-3 text-red-500"
                                              strokeWidth={2}
                                            />
                                          ) : (
                                            <LocalBarOutlinedIcon className="!h-[14px] !w-[14px] mr-1" />
                                          )}
                                          {breakTime.start_at} -{" "}
                                          {breakTime.end_at}
                                        </span>
                                      </div>
                                    )
                                  )}
                                </div>
                            )}

                            {showWorkingAreas && (
                              <div className="mt-1 min-w-0">
                                {slot.working_area_id ? (
                                  <CustomTooltipComponent title={slot.working_area_name || ""} placement="top" arrow>
                                    {slot.working_area_color ? (
                                      <div
                                        className="flex min-w-0 items-center gap-1.5 rounded-md border px-2 py-1"
                                        style={{
                                          color: slot.working_area_color,
                                          backgroundColor: `${slot.working_area_color}1F`,
                                          borderColor: slot.working_area_color,
                                        }}
                                      >
                                        <span className="truncate text-[11px] font-semibold uppercase">
                                          {slot.working_area_name}
                                        </span>
                                      </div>
                                    ) : (
                                      <div className="flex min-w-0 items-center gap-1.5 rounded-md bg-green-100 border border-green-700 px-2 py-1 text-green-700">
                                        <span className="truncate text-[11px] font-semibold uppercase">
                                          {slot.working_area_name}
                                        </span>
                                      </div>
                                    )}
                                  </CustomTooltipComponent>
                                ) : (
                                  <div className="flex items-center gap-1.5 px-2 py-1 text-[11px] text-gray-400">
                                    <MapPinIcon className="h-3.5 w-3.5 shrink-0" />
                                    <span>Nessuna area associata</span>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ))
                    ) : (
                      <p className="text-xs text-gray-500">
                        Nessun orario disponibile
                      </p>
                    )}
                  </div>

                  {/* FOOTER */}
                  <div className="flex flex-col items-center px-2 pb-2 pt-1">
                    {isFisso ? (
                      <div className="text-xs font-semibold mt-1 text-indigo-600 uppercase">
                        Turno fisso
                      </div>
                    ) : (
                      <>
                        {(() => {
                          const showButtons =
                            !selectedUnit || shift?.ref_ou === selectedUnit;
                          if (!showButtons) return null;
                          return (
                            <div className="flex items-center gap-2 mb-1">
                              {shift.typology === "custom" && (
                                <CustomTooltipComponent
                                  title="Modifica Unità"
                                  placement="top-start"
                                >
                                  <PencilSquareIcon
                                    className="text-gray-900 hover:text-indigo-600 cursor-pointer w-[19px] h-[19px]"
                                    onClick={(e) => {
                                      if (dayBlocked) {
                                        e.preventDefault();
                                        return;
                                      }
                                      handleEditOuPairing(dipendente.id, shift);
                                    }}
                                  />
                                </CustomTooltipComponent>
                              )}
                              <CustomTooltipComponent
                                title="Elimina turno"
                                placement="top-start"
                              >
                                <TrashIcon
                                  className="text-gray-900 hover:text-red-600 cursor-pointer w-[19px] h-[19px]"
                                  onClick={() =>
                                    handleDeleteDailyWh(
                                      dipendente.id,
                                      shift.dailyWorkingHourId,
                                      dipendente.cf,
                                      formattedDay,
                                      dipendente.last_name || "",
                                      dipendente.first_name || "",
                                      shift.status,
                                      shift?.ref_ou
                                    )
                                  }
                                />
                              </CustomTooltipComponent>
                            </div>
                          );
                        })()}

                        {!shift.status && (
                          <button
                            className="py-1 mt-1 bg-orange-400 w-full text-white uppercase rounded-md shadow-md text-xs"
                            onClick={(e) => {
                              if (dayBlocked) {
                                e.preventDefault();
                                return;
                              }
                              handleConfirmSingleShift(
                                dipendente,
                                shift.dailyWorkingHourId
                              );
                            }}
                          >
                            Conferma
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
        </>
      ) : (
        <div className="absolute top-0 left-0 right-0 bottom-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity duration-300">
          {(workingHour?.week_hours && workingHour?.has_active_contract) ||
          (workingHour.shift_type === "a_chiamata" &&
            workingHour?.has_active_contract) ? (
            <PlusCircleIcon
              className="text-indigo-500 hover:bg-indigo-50 rounded-2xl hover:text-indigo-500 cursor-pointer w-10 h-10"
              onClick={() =>
                handlePlusCircleClick(
                  dipendente.id,
                  formattedDay,
                  dipendente.cf,
                  dipendente.last_name || "",
                  dipendente.first_name || "",
                  selectedUnit || ""
                )
              }
            />
          ) : (
            <div className="absolute top-0 left-0 right-0 bottom-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity duration-300">
              <div className="flex flex-col items-center justify-center text-gray-500">
                <NoSymbolIcon className="w-8 h-8 text-red-500 mb-2" />
                <p>Orario lavorativo non presente</p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}